import React, { useState } from 'react';
import { X, Send, Loader2, Mail, Link, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareModal - Reusable modal for sharing links via copy or email
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - title: string
 * - subtitle: string
 * - shareUrl: string
 * - onSendEmail: async function(email, message) - called when user sends email
 * - lang: 'en' | 'es'
 * - emailSubject: string (optional)
 * - defaultMessage: string (optional)
 */
const ShareModal = ({ 
  isOpen, 
  onClose, 
  title, 
  subtitle, 
  shareUrl, 
  onSendEmail,
  lang = 'en',
  emailSubject = '',
  defaultMessage = '',
  currentState = null  // SSOT values from Results page
}) => {
  const [activeTab, setActiveTab] = useState('link'); // 'link' or 'email'
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  
  // Format currency helper
  const formatDollars = (val) => {
    if (val === undefined || val === null) return 'N/A';
    return Math.round(val).toLocaleString();
  };

  const handleCopy = async () => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success(lang === 'en' ? 'Link copied to clipboard!' : '¡Enlace copiado!');
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for environments where Clipboard API is blocked
        throw new Error('Clipboard API not available');
      }
    } catch (error) {
      console.warn('Clipboard copy failed:', error);
      // Fallback: show the URL in a selectable input and prompt user to copy manually
      toast.info(
        lang === 'en' 
          ? 'Auto-copy unavailable. Please select and copy the link manually.' 
          : 'Copiar automático no disponible. Selecciona y copia el enlace manualmente.',
        { duration: 4000 }
      );
      // Focus and select the link input for easy manual copy
      const linkInput = document.querySelector('[data-testid="share-link-input"]');
      if (linkInput) {
        linkInput.select();
      }
    }
  };

  const handleSendEmail = async () => {
    if (!email.trim()) {
      toast.error(lang === 'en' ? 'Please enter an email address' : 'Ingresa un correo electrónico');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(lang === 'en' ? 'Please enter a valid email address' : 'Ingresa un correo válido');
      return;
    }

    setSending(true);
    try {
      await onSendEmail(email, message);
      setSent(true);
      toast.success(lang === 'en' ? 'Email sent successfully!' : '¡Correo enviado!');
      setTimeout(() => {
        setEmail('');
        setMessage(defaultMessage);
        setSent(false);
      }, 2000);
    } catch (error) {
      toast.error(lang === 'en' ? 'Failed to send email' : 'Error al enviar correo');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="share-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {subtitle && (
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          )}
        </div>
        
        {/* Current Analysis State Summary - SSOT display */}
        {currentState && (
          <div className="p-4 bg-slate-50 border-b border-slate-200" data-testid="share-current-state">
            <div className="text-xs font-medium text-slate-500 mb-2">
              {lang === 'en' ? 'Current Analysis State:' : 'Estado Actual del Análisis:'}
            </div>
            {/* Valuation-specific metrics */}
            {currentState.strategy === 'valuation' ? (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between col-span-2">
                  <span className="text-slate-600">{lang === 'en' ? 'Type:' : 'Tipo:'}</span>
                  <span className="font-semibold text-blue-600">
                    {lang === 'en' ? 'PROPERTY VALUATION' : 'VALUACIÓN DE PROPIEDAD'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Estimated Value:' : 'Valor Estimado:'}</span>
                  <span className="font-semibold text-primary">${formatDollars(currentState.arv)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Confidence:' : 'Confianza:'}</span>
                  <span className={`font-semibold ${
                    currentState.arvConfidence === 'high' ? 'text-green-600' :
                    currentState.arvConfidence === 'medium' ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {currentState.arvConfidence === 'high' ? (lang === 'en' ? 'HIGH' : 'ALTO') :
                     currentState.arvConfidence === 'medium' ? (lang === 'en' ? 'MEDIUM' : 'MEDIO') :
                     (lang === 'en' ? 'LOW' : 'BAJO')}
                  </span>
                </div>
                {currentState.purchasePrice > 0 && (
                  <div className="flex justify-between col-span-2">
                    <span className="text-slate-600">{lang === 'en' ? 'Potential Equity:' : 'Equidad Potencial:'}</span>
                    <span className={`font-semibold ${currentState.potentialEquity >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${formatDollars(currentState.potentialEquity)}
                    </span>
                  </div>
                )}
              </div>
            ) : currentState.strategy === 'rental' ? (
              /* Rental metrics */
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between col-span-2">
                  <span className="text-slate-600">{lang === 'en' ? 'Type:' : 'Tipo:'}</span>
                  <span className="font-semibold text-green-600">
                    {lang === 'en' ? 'RENTAL ANALYSIS' : 'ANÁLISIS DE ALQUILER'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Decision:' : 'Decisión:'}</span>
                  <span className={`font-semibold ${
                    currentState.dealDecision === 'BUY' || currentState.dealDecision === 'COMPRAR' ? 'text-green-600' :
                    currentState.dealDecision === 'NEGOTIATE' || currentState.dealDecision === 'NEGOCIAR' ? 'text-amber-600' : 'text-red-600'
                  }`}>{currentState.dealDecision}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Cash Flow:' : 'Flujo:'}</span>
                  <span className={`font-semibold ${(currentState.monthlyCashflow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${formatDollars(currentState.monthlyCashflow || 0)}/mo
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Cap Rate:' : 'Tasa Cap:'}</span>
                  <span className="font-semibold">{(currentState.capRate || 0).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">DSCR:</span>
                  <span className="font-semibold">{(currentState.dscr || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between col-span-2">
                  <span className="text-slate-600">{lang === 'en' ? 'Financing:' : 'Financiamiento:'}</span>
                  <span className={`font-semibold px-2 py-0.5 rounded text-xs ${
                    currentState.financingMode === 'cash' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {currentState.financingMode === 'cash' ? (lang === 'en' ? 'CASH' : 'EFECTIVO') : 'DSCR'}
                  </span>
                </div>
              </div>
            ) : (
              /* Flip metrics */
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Decision:' : 'Decisión:'}</span>
                  <span className={`font-semibold ${
                    currentState.dealDecision === 'BUY' || currentState.dealDecision === 'COMPRAR' ? 'text-green-600' :
                    currentState.dealDecision === 'NEGOTIATE' || currentState.dealDecision === 'NEGOCIAR' ? 'text-amber-600' : 'text-red-600'
                  }`}>{currentState.dealDecision}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Score:' : 'Puntuación:'}</span>
                  <span className="font-semibold">{Math.round(currentState.dealScore || 0)}/100</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Net Profit:' : 'Ganancia:'}</span>
                  <span className={`font-semibold ${currentState.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${formatDollars(currentState.netProfit)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{lang === 'en' ? 'Loan Costs:' : 'Costos Préstamo:'}</span>
                  <span className="font-semibold">${formatDollars(currentState.loanCosts)}</span>
                </div>
                <div className="flex justify-between col-span-2">
                  <span className="text-slate-600">{lang === 'en' ? 'Financing:' : 'Financiamiento:'}</span>
                  <span className={`font-semibold px-2 py-0.5 rounded text-xs ${
                    currentState.financingMode === 'cash' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {currentState.financingMode === 'cash' ? (lang === 'en' ? 'CASH' : 'EFECTIVO') : (lang === 'en' ? 'FINANCED' : 'FINANCIADO')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('link')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'link' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <Link className="w-4 h-4" />
            {lang === 'en' ? 'Copy Link' : 'Copiar Enlace'}
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'email' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <Mail className="w-4 h-4" />
            {lang === 'en' ? 'Send Email' : 'Enviar Correo'}
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {activeTab === 'link' ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 font-mono truncate"
                  data-testid="share-link-input"
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopy}
                  className={`px-4 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    copied 
                      ? 'bg-green-500 text-white' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied 
                    ? (lang === 'en' ? 'Copied!' : '¡Copiado!')
                    : (lang === 'en' ? 'Copy' : 'Copiar')}
                </button>
              </div>
              <p className="text-xs text-slate-500 text-center">
                {lang === 'en' 
                  ? 'Anyone with this link can view this content'
                  : 'Cualquier persona con este enlace puede ver este contenido'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {lang === 'en' ? 'Recipient Email' : 'Correo del Destinatario'}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={lang === 'en' ? 'partner@email.com' : 'socio@correo.com'}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {lang === 'en' ? 'Message (Optional)' : 'Mensaje (Opcional)'}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={lang === 'en' ? 'Add a personal message...' : 'Agregar un mensaje personal...'}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                />
              </div>
              <button
                onClick={handleSendEmail}
                disabled={sending || sent}
                className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                  sent 
                    ? 'bg-green-500 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                }`}
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : sent ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {sending 
                  ? (lang === 'en' ? 'Sending...' : 'Enviando...')
                  : sent 
                    ? (lang === 'en' ? 'Sent!' : '¡Enviado!')
                    : (lang === 'en' ? 'Send Email' : 'Enviar Correo')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
